import { Visibility, VisibilityOff } from '@mui/icons-material'
import { Box, Button, CircularProgress, Grid, IconButton, InputAdornment, TextField, Typography } from '@mui/material'
import React, { useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { useResetPasswordMutation } from '../../../Redux/api/adminApi'
import { useToast } from '../../../Hooks/useToast'

interface ResetForm {
    password: string | undefined
    passwordConfirmation: string | undefined
}

export const ResetPassword = () => {
    /* ******************** Hooks ******************** */
    const {locator, token} = useParams()
    const navigate = useNavigate();
    const {showToast} = useToast();
    const {handleSubmit, control, formState: {isDirty}} = useForm<ResetForm>({
        defaultValues: {
            password: '',
            passwordConfirmation: ''
        }
    })
    const [resetPassword, {data: resetResponse, isLoading: resetLoading}] = useResetPasswordMutation();
    /* ******************** Variables ******************** */
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConf, setShowPasswordConf] = useState(false);
    /* ******************** Functions ******************** */
    const onSubmit = (data: ResetForm) => {
        if (data?.password !== data?.passwordConfirmation) {
            showToast({
                text: "Passwords must match",
                variant: 'error'
            })
        }
        else if (locator && token) {
            resetPassword({
                locator,
                token,
                password: data.password!
            })
            .unwrap()
            .then(res => navigate('/login'))
            .catch( err =>{
                console.log(err)
                showToast({
                    text: "Unable to reset password",
                    variant: 'error'
                })
            })
        }
    }
    /* ******************** Effects ******************** */
    /* ******************** JSX ******************** */
    return (
        <Grid
            bgcolor={'green.50'}
            width={"100%"}
            height={"100%"}
            overflow={"hidden"}
            display={"grid"}
            gridTemplateRows={"repeat(2, min-content)"}
            justifyItems={"center"}
            paddingY={14}
        >
            
            <Grid item  display={"flex"} flexDirection={"column"} justifyContent={"center"} alignItems={"center"}>
                <Typography variant="lora-display-small" marginBottom={"16px"}>Update your password</Typography>
            </Grid>
            <Grid 
                item 
                xs="auto" 
            > 
            
                <Box                               
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-start",
                        alignItems: "center",
                        height: "fit-content"
                    }}
                > 
                    <Typography variant="body-large" marginBottom={"30px"}>Enter your new password</Typography>
                    <Controller
                        name="password"
                        control={control} 
                        render={({field})=>(
                            <TextField
                                {...field}
                                placeholder="New password"
                                type={!showPassword?"Password":"text"}
                                sx={{
                                    marginBottom: "16px",
                                    width: "300px"
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? <Visibility color="primary"/> : <VisibilityOff color="primary"/>}
                                        </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        )}
                    />
                    <Controller
                        name="passwordConfirmation"
                        control={control} 
                        render={({field})=>(
                            <TextField
                                {...field}
                                placeholder="Confirm new password"
                                type={!showPasswordConf?"Password":"text"}
                                sx={{
                                    marginBottom: "16px",
                                    width: "300px"
                                }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                        <IconButton
                                            aria-label="toggle password visibility"
                                            onClick={() => setShowPasswordConf(!showPasswordConf)}
                                        >
                                            {showPasswordConf ? <Visibility color="primary"/> : <VisibilityOff color="primary"/>}
                                        </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        )}
                    />
                    <Button 
                        variant="contained" 
                        disabled={!isDirty} 
                        size="large" 
                        onClick={handleSubmit(onSubmit)}
                        endIcon={resetLoading? <CircularProgress color="secondary" size={"18px"}/> :null} 
                    >
                        {resetLoading? "Updating..." : "Update"}
                    </Button>
                </Box>
            </Grid>
        </Grid>
    )
}